import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronRightIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './collapsible';
import { navRowBase, navRowSizes } from './nav-row';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

/**
 * SidebarMenu（侧边栏纵向导航菜单）。
 *
 * 对齐 W3C APG 的 [Disclosure Navigation Menu]
 * (https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/)：
 * `nav > ul > li`，可展开的分组是一个带 `aria-expanded` 的按钮加就地展开的子列表，
 * 键盘走**普通 Tab 序**——这是它与 Navigation Treeview 模式的关键差别，菜单因此
 * 不需要 roving tabindex 那套焦点管理。
 *
 * 于是这里没有「逻辑」需要自研，全部外包给 Base UI（组件来源分层原则第 1 级）：
 * 展开/收起与 `aria-expanded`/`aria-controls` 联动交给 `Collapsible`，折叠成图标条后的
 * 飞出层交给 `Popover`（定位、碰撞翻转、dismiss、焦点归还），行本体就是原生
 * `<button>`/`<a>`，键盘激活由浏览器负责。缩进也不再需要层级计算，靠 DOM 嵌套天然累积。
 *
 * 本文件曾复用 `tree.tsx` 的 `@headless-tree` 底座（Navigation Treeview 模式），代价是
 * 要覆盖它「单击即展开」的文件树行为、在折叠态强行收起展开状态，且容器级 hotkeys 会
 * `preventDefault` 掉 Enter、连带废掉飞出层里的键盘激活。决策记录见
 * `docs/architecture/16-UI组件库.md`。
 */

const DEFAULT_INDENT = 12;

const sidebarMenuItemVariants = cva(
  [
    navRowBase,
    'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50',
    // 当前项只由行主体的 `aria-current` 表达，行的高亮从它反推，避免两处状态。
    'has-[[aria-current]]:bg-accent has-[[aria-current]]:text-accent-foreground has-[[aria-current]]:font-medium',
  ],
  {
    variants: {
      size: navRowSizes,
      collapsed: {
        true: 'justify-center px-0',
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      collapsed: false,
    },
  },
);

type SidebarMenuContextValue = {
  collapsed: boolean;
  size: 'sm' | 'md';
  indent: number;
};

const SidebarMenuContext = React.createContext<SidebarMenuContextValue>({
  collapsed: false,
  size: 'md',
  indent: DEFAULT_INDENT,
});

/** 可展开项的标签：`SidebarMenuSubTrigger` 与折叠态飞出层的标题共用一个来源。 */
const SidebarMenuSubContext = React.createContext<{ label: React.ReactNode }>({
  label: null,
});

/** 飞出层内的行导航后要顺手关掉浮层；层外为 null。 */
const SidebarMenuFlyoutContext = React.createContext<(() => void) | null>(null);

interface SidebarMenuProps extends React.ComponentProps<'nav'> {
  /**
   * 折叠为图标条：隐藏文字（叶子行的标签走 Tooltip），可展开项的子级改由
   * 飞出层承载——图标条只有一列宽，缩进表达不了层级。
   */
  collapsed?: boolean;
  size?: 'sm' | 'md';
  /** 每层缩进（px），默认 12。 */
  indent?: number;
}

function SidebarMenu({
  collapsed = false,
  size = 'md',
  indent = DEFAULT_INDENT,
  className,
  children,
  ...props
}: SidebarMenuProps) {
  const value = React.useMemo(
    () => ({ collapsed, size, indent }),
    [collapsed, size, indent],
  );
  return (
    <SidebarMenuContext.Provider value={value}>
      <TooltipProvider>
        <nav
          data-slot="sidebar-menu"
          data-collapsed={collapsed || undefined}
          className={cn('flex min-w-0 flex-col gap-3', className)}
          {...props}
        >
          {children}
        </nav>
      </TooltipProvider>
    </SidebarMenuContext.Provider>
  );
}

function SidebarMenuGroup({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-menu-group"
      className={cn('flex min-w-0 flex-col gap-0.5', className)}
      {...props}
    />
  );
}

/** 分区标题。折叠态下隐藏（图标条里没有横向空间）。 */
function SidebarMenuGroupLabel({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const { collapsed } = React.useContext(SidebarMenuContext);
  if (collapsed) return null;
  return (
    <div
      data-slot="sidebar-menu-group-label"
      className={cn(
        'text-muted-foreground px-2 py-1 text-xs font-medium',
        className,
      )}
      {...props}
    />
  );
}

/** 菜单列表。子级列表也用它，嵌在 `SidebarMenuSubContent` 里即可。 */
function SidebarMenuList({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-list"
      className={cn('flex min-w-0 list-none flex-col gap-0.5', className)}
      {...props}
    />
  );
}

interface SidebarMenuItemProps
  extends
    React.ComponentProps<'li'>,
    Pick<VariantProps<typeof sidebarMenuItemVariants>, 'size'> {}

/**
 * 叶子行。视觉高亮落在 `<li>` 而非内层按钮，使 hover/选中能覆盖整行（含徽标与尾部动作）；
 * 焦点环用 `has-[:focus-visible]` 从内层借。
 */
function SidebarMenuItem({ size, className, ...props }: SidebarMenuItemProps) {
  const { collapsed, size: ctxSize } = React.useContext(SidebarMenuContext);
  return (
    <li
      data-slot="sidebar-menu-item"
      className={cn(
        sidebarMenuItemVariants({ size: size ?? ctxSize, collapsed }),
        className,
      )}
      {...props}
    />
  );
}

interface SidebarMenuButtonProps extends Omit<
  React.ComponentProps<'button'>,
  'children'
> {
  /** 行首图标，折叠态下是唯一可见内容。 */
  icon?: React.ReactNode;
  children?: React.ReactNode;
  /** 当前项：转成 `aria-current` 并点亮整行。 */
  active?: boolean;
  /** 折叠态的 Tooltip 内容，默认取 `children`。 */
  tooltip?: React.ReactNode;
  /**
   * 换用别的元素渲染（Base UI 的 `render` 约定），如 `render={<a href="/x" />}`
   * 把菜单项变成真链接——导航菜单里这才是首选。
   */
  render?: React.ReactElement<Record<string, unknown>>;
}

/** 行的可交互主体。默认 `<button>`，`render` 可换成 `<a>`。 */
function SidebarMenuButton({
  icon,
  children,
  active,
  tooltip,
  render,
  className,
  onClick,
  ...props
}: SidebarMenuButtonProps) {
  const { collapsed } = React.useContext(SidebarMenuContext);
  const closeFlyout = React.useContext(SidebarMenuFlyoutContext);
  const label = tooltip ?? children;

  const merged: Record<string, unknown> = {
    ...props,
    'data-slot': 'sidebar-menu-button',
    'aria-current': active ? 'page' : undefined,
    // 折叠成图标条后行内没有文字，可访问名只能由这里补。
    'aria-label':
      collapsed && typeof label === 'string' ? label : props['aria-label'],
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      closeFlyout?.();
    },
    className: cn(
      // `text-left` 不能只靠 <li> 继承：<button> 的 UA 默认 text-align 是 center。
      // `h-full` + `flex-1`：主体要铺满行的可点区域，否则「hover 高亮的是整行、能点的
      // 只有图标」——折叠态尤其明显，且 Tooltip 会以图标而非整行为锚点。
      'flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2 bg-transparent text-left text-inherit outline-none',
      collapsed && 'justify-center',
      className,
    ),
  };

  const content = (
    <>
      {icon}
      {collapsed ? null : (
        <span className="min-w-0 flex-1 truncate">{children}</span>
      )}
    </>
  );

  const element = render ? (
    React.cloneElement(
      render,
      {
        ...render.props,
        ...merged,
        className: cn(
          merged.className as string,
          (render.props as { className?: string }).className,
        ),
      },
      content,
    )
  ) : (
    <button type="button" {...merged}>
      {content}
    </button>
  );

  if (!collapsed || !label) return element;

  return (
    <Tooltip>
      <TooltipTrigger render={element} />
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

interface SidebarMenuSubProps extends Omit<
  React.ComponentProps<'li'>,
  'children'
> {
  /** 项的标签，`SidebarMenuSubTrigger` 与折叠态飞出层的标题都取它。 */
  label: React.ReactNode;
  /** 非受控展开态初值（仅就地展开形态生效）。 */
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

/**
 * 可展开项。展开态是 `Collapsible`（就地展开子级），折叠成图标条后换成 `Popover`
 * （飞出层）——两种形态下消费方写同一份结构。
 *
 * 飞出层的开合是它自己的瞬时状态，不写回 `open`/`defaultOpen`：图标条上点开一次飞出层
 * 不该改变侧栏展开后的层级形态。
 */
function SidebarMenuSub({
  label,
  defaultOpen,
  open,
  onOpenChange,
  className,
  children,
  ...props
}: SidebarMenuSubProps) {
  const { collapsed } = React.useContext(SidebarMenuContext);
  const subValue = React.useMemo(() => ({ label }), [label]);
  const [flyoutOpen, setFlyoutOpen] = React.useState(false);
  const closeFlyout = React.useCallback(() => setFlyoutOpen(false), []);

  return (
    <SidebarMenuSubContext.Provider value={subValue}>
      {collapsed ? (
        <li
          data-slot="sidebar-menu-sub"
          className={cn('min-w-0', className)}
          {...props}
        >
          <Popover open={flyoutOpen} onOpenChange={setFlyoutOpen}>
            <SidebarMenuFlyoutContext.Provider value={closeFlyout}>
              {children}
            </SidebarMenuFlyoutContext.Provider>
          </Popover>
        </li>
      ) : (
        <Collapsible
          defaultOpen={defaultOpen}
          open={open}
          onOpenChange={onOpenChange}
          render={
            <li
              data-slot="sidebar-menu-sub"
              className={cn('min-w-0', className)}
              {...props}
            />
          }
        >
          {children}
        </Collapsible>
      )}
    </SidebarMenuSubContext.Provider>
  );
}

interface SidebarMenuSubTriggerProps
  extends
    Omit<React.ComponentProps<'button'>, 'children'>,
    Pick<VariantProps<typeof sidebarMenuItemVariants>, 'size'> {
  icon?: React.ReactNode;
  /** 覆盖行文案，默认取 `SidebarMenuSub` 的 `label`。 */
  children?: React.ReactNode;
  /** 折叠态下悬停即弹出飞出层（图标条上这是默认预期）。仅折叠态生效。 */
  openOnHover?: boolean;
  /** 悬停多久才弹出 / 移开多久才收起（ms），仅 `openOnHover` 时生效。 */
  delay?: number;
  closeDelay?: number;
}

/**
 * 可展开项的行。展开态是 `Collapsible.Trigger`（原生 button + `aria-expanded`），
 * 折叠态是 `Popover.Trigger`；chevron 跟 `data-panel-open` 转向。
 */
function SidebarMenuSubTrigger({
  icon,
  children,
  size,
  className,
  openOnHover = true,
  delay = 150,
  closeDelay = 150,
  ...props
}: SidebarMenuSubTriggerProps) {
  const { collapsed, size: ctxSize } = React.useContext(SidebarMenuContext);
  const { label } = React.useContext(SidebarMenuSubContext);
  const rowClassName = cn(
    sidebarMenuItemVariants({ size: size ?? ctxSize, collapsed }),
    'cursor-pointer bg-transparent focus-visible:ring-2 focus-visible:ring-ring/50',
    // chevron 跟展开态转向；写法同 accordion.tsx，靠 Base UI 给的 data-panel-open。
    '[&[data-panel-open]>svg:last-child]:rotate-90',
    className,
  );

  if (collapsed) {
    return (
      <PopoverTrigger
        data-slot="sidebar-menu-sub-trigger"
        aria-label={typeof label === 'string' ? label : undefined}
        className={rowClassName}
        openOnHover={openOnHover}
        delay={delay}
        // 触发器与浮层之间有 sideOffset 的间隙，收起给点宽限期，免得鼠标穿过时就关了。
        closeDelay={closeDelay}
        {...props}
      >
        {icon}
      </PopoverTrigger>
    );
  }

  return (
    <CollapsibleTrigger
      data-slot="sidebar-menu-sub-trigger"
      className={rowClassName}
      {...props}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children ?? label}</span>
      <ChevronRightIcon className="text-muted-foreground pointer-events-none size-3.5 shrink-0 transition-transform duration-150" />
    </CollapsibleTrigger>
  );
}

/**
 * 可展开项的子级容器。展开态是 `Collapsible.Panel`（高度动画走 Base UI 给的
 * `--collapsible-panel-height`），折叠态是飞出层；飞出层里把 `collapsed` 复位，
 * 于是层级又能就地展开、写法与展开态完全一致。
 */
function SidebarMenuSubContent({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  const ctx = React.useContext(SidebarMenuContext);
  const { label } = React.useContext(SidebarMenuSubContext);
  const expandedCtx = React.useMemo(
    () => ({ ...ctx, collapsed: false }),
    [ctx],
  );

  if (ctx.collapsed) {
    return (
      <PopoverContent
        data-slot="sidebar-menu-sub-content"
        side="right"
        align="start"
        sideOffset={8}
        className={cn('w-auto min-w-44 p-1', className)}
        {...props}
      >
        <SidebarMenuContext.Provider value={expandedCtx}>
          {label ? (
            <div className="text-muted-foreground px-2 py-1 text-xs font-medium">
              {label}
            </div>
          ) : null}
          {children}
        </SidebarMenuContext.Provider>
      </PopoverContent>
    );
  }

  return (
    <CollapsibleContent
      data-slot="sidebar-menu-sub-content"
      className="h-(--collapsible-panel-height) overflow-hidden transition-[height] duration-150 ease-out data-ending-style:h-0 data-starting-style:h-0"
    >
      <div
        className={cn('flex min-w-0 flex-col pt-0.5', className)}
        style={{ paddingLeft: ctx.indent }}
        {...props}
      >
        {children}
      </div>
    </CollapsibleContent>
  );
}

/** 计数/状态徽标。折叠态下隐藏。 */
function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  const { collapsed } = React.useContext(SidebarMenuContext);
  if (collapsed) return null;
  return (
    <span
      data-slot="sidebar-menu-badge"
      className={cn(
        'text-muted-foreground ml-auto shrink-0 text-xs tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * 尾部动作按钮（如「更多」）。与行主体是**兄弟节点**而非嵌套，所以点它不会触发行导航，
 * 不需要拦冒泡（拦了反而会挡掉 Base UI 判定点击完成所需的事件，浮层打不开）。
 * hover 或行内聚焦时才显形；折叠态下隐藏。
 *
 * 可作为浮层触发器：`<DropdownMenuTrigger render={<SidebarMenuAction />} />`。
 */
function SidebarMenuAction({
  className,
  ...props
}: React.ComponentProps<'button'>) {
  const { collapsed } = React.useContext(SidebarMenuContext);
  if (collapsed) return null;
  return (
    <button
      type="button"
      data-slot="sidebar-menu-action"
      className={cn(
        'text-muted-foreground hover:bg-background hover:text-foreground group-hover/nav-row:opacity-100 flex size-5 shrink-0 cursor-pointer items-center justify-center rounded opacity-0 transition-opacity focus-visible:opacity-100 data-popup-open:opacity-100',
        className,
      )}
      {...props}
    />
  );
}

export type {
  SidebarMenuButtonProps,
  SidebarMenuItemProps,
  SidebarMenuProps,
  SidebarMenuSubProps,
  SidebarMenuSubTriggerProps,
};
export {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuGroup,
  SidebarMenuGroupLabel,
  SidebarMenuItem,
  sidebarMenuItemVariants,
  SidebarMenuList,
  SidebarMenuSub,
  SidebarMenuSubContent,
  SidebarMenuSubTrigger,
};
